/**
 * Image field — file upload, drag-and-drop, or a URL.
 *
 * The value IS the asset, not a reference to one. An earlier version stored an id into a
 * shared document-level map, which meant a duplicated block pointed at the same record:
 * changing the picture on the copy silently changed the original. Holding the asset in the
 * block's own props makes duplication a real copy.
 *
 * Dimensions and weight are read from the file itself rather than typed by the author. They
 * are not decoration: width and height become the attributes that let the browser reserve
 * space (the CLS half of Core Web Vitals), and the exporter refuses an image without them.
 */

import { useRef, useState } from 'react';
import type { CustomFieldRender } from '@puckeditor/core';
import type { AssetRef } from '@lp/shared';
import { isDataUrl, processFile, TARGET_BYTES } from '../images/processFile.js';

type Props = Parameters<CustomFieldRender<AssetRef | null>>[0] & {
  role?: 'hero' | 'inline' | 'og';
};

function filenameOf(url: string): string {
  try {
    return decodeURIComponent(new URL(url).pathname.split('/').pop() ?? '') || 'image';
  } catch {
    return 'image';
  }
}

const newId = (): string => `asset-${Math.random().toString(36).slice(2, 10)}`;

export function ImageField({ value, onChange, readOnly, role }: Props): React.ReactElement {
  const asset = value ?? null;

  const [url, setUrl] = useState(asset && !isDataUrl(asset.url) ? asset.url : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  async function pick(file: File): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const img = await processFile(file);
      onChange({
        id: newId(),
        url: img.dataUrl,
        width: img.width,
        height: img.height,
        bytes: img.bytes,
        // Alt describes the picture, so a new picture starts without one.
        alt: '',
        filename: img.filename,
        source: { provider: 'upload', author: '', authorUrl: '' },
      });
      setUrl('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read that file.');
    } finally {
      setBusy(false);
    }
  }

  async function attachUrl(nextUrl: string): Promise<void> {
    if (!nextUrl.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Could not load that image.'));
        img.src = nextUrl;
      });

      let bytes = 0;
      try {
        const head = await fetch(nextUrl, { method: 'HEAD' });
        bytes = Number(head.headers.get('content-length') ?? 0);
      } catch {
        // Cross-origin HEAD usually fails. Weight is advisory, so carry on without it.
      }

      onChange({
        id: newId(),
        url: nextUrl,
        width: img.naturalWidth,
        height: img.naturalHeight,
        bytes,
        alt: '',
        filename: filenameOf(nextUrl),
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load that image.');
    } finally {
      setBusy(false);
    }
  }

  const notHosted = asset ? isDataUrl(asset.url) : false;

  return (
    <div className="lpf">
      <div
        className={`lpf__drop${dragging ? ' lpf__drop--over' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          const file = e.dataTransfer.files[0];
          if (file && !readOnly) void pick(file);
        }}
        onClick={() => !readOnly && fileInput.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') fileInput.current?.click();
        }}
      >
        {busy ? 'Processing…' : 'Drop an image here, or click to choose a file'}
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void pick(file);
            e.target.value = '';
          }}
        />
      </div>

      <label className="lpf__row">
        <span className="lpf__label">
          …or paste a URL
          <span className="lpf__hint">Unsplash, Pexels, or a Webflow asset link</span>
        </span>
        <input
          className="lpf__input"
          value={url}
          disabled={readOnly || busy}
          placeholder="https://…"
          onChange={(e) => setUrl(e.target.value)}
          onBlur={() => url && url !== asset?.url && attachUrl(url)}
        />
      </label>

      {error && <span className="lpf__error">{error}</span>}

      {asset && (
        <>
          <div className="lpf__preview">
            <img src={asset.url} alt="" />
          </div>

          {notHosted && (
            <span className="lpf__notice">
              Not hosted yet — fine for laying the page out, but it can’t be exported to
              Webflow. Upload it to Webflow’s Assets panel and paste the URL, or wait for the
              upload server.
            </span>
          )}

          <label className="lpf__row">
            <span className="lpf__label">
              Alt text
              <span className="lpf__hint">
                Describe it naturally; include the keyword if it fits
              </span>
            </span>
            <input
              className="lpf__input"
              value={asset.alt}
              disabled={readOnly}
              onChange={(e) => onChange({ ...asset, alt: e.target.value })}
            />
          </label>

          <div className="lpf__meta">
            <span>
              {asset.width}×{asset.height}
            </span>
            {asset.bytes > 0 && (
              <span className={asset.bytes > TARGET_BYTES ? 'lpf__meta--warn' : undefined}>
                {Math.round(asset.bytes / 1024)} KB
                {asset.bytes > TARGET_BYTES && ' — over the 200KB guideline'}
              </span>
            )}
            {role === 'hero' && <span>Loads eagerly (LCP)</span>}
            <button
              type="button"
              className="lp-btn lp-btn--ghost"
              disabled={readOnly}
              onClick={() => onChange(null)}
            >
              Remove
            </button>
          </div>

          {!asset.alt.trim() && (
            <span className="lpf__error">
              No alt text. Screen readers and Google both need it.
            </span>
          )}
        </>
      )}
    </div>
  );
}
